import axios, { AxiosError } from 'axios';

/**
 * Project Type Service
 *
 * Handles all project type-related API requests to the backend.
 */

// API Base URL from environment variable or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

// Project Type endpoints
const PROJECT_TYPE_ENDPOINTS = {
  list: `${API_BASE_URL}/project-types`,
  byId: (id: string) => `${API_BASE_URL}/project-types/${id}`,
  categories: `${API_BASE_URL}/project-types/categories`,
  byCategory: (category: string) => `${API_BASE_URL}/project-types/category/${category}`,
  create: `${API_BASE_URL}/project-types`,
  update: (id: string) => `${API_BASE_URL}/project-types/${id}`,
  delete: (id: string) => `${API_BASE_URL}/project-types/${id}`
};

/**
 * Project Type interface matching backend
 */
export interface ProjectType {
  id: string;
  name: string;
  category: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at?: string | null;
}

/**
 * Project Type creation input
 */
export interface CreateProjectTypeInput {
  name: string;
  category: string;
  description?: string;
  icon?: string;
  color?: string;
  sort_order?: number;
}

/**
 * Project Type update input
 */
export interface UpdateProjectTypeInput {
  name?: string;
  category?: string;
  description?: string;
  icon?: string;
  color?: string;
  sort_order?: number;
  is_active?: boolean;
}

/**
 * List project types query parameters
 */
export interface ListProjectTypesParams {
  category?: string;
  is_active?: boolean;
}

/**
 * API Response interfaces
 */
export interface ProjectTypesListResponse {
  success: boolean;
  data: ProjectType[];
  count: number;
}

export interface ProjectTypeResponse {
  success: boolean;
  data: ProjectType;
}

export interface CategoriesResponse {
  success: boolean;
  data: string[];
  count: number;
}

export interface MessageResponse {
  success: boolean;
  message: string;
}

export interface APIError {
  error: string;
  details?: string;
}

/**
 * Handle API errors
 */
function handleAPIError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<APIError>;
    const errorMessage = axiosError.response?.data?.error || axiosError.message;
    throw new Error(errorMessage);
  }
  throw error;
}

/**
 * List all project types
 */
export async function listProjectTypes(
  params: ListProjectTypesParams,
  token: string
): Promise<ProjectType[]> {
  try {
    const response = await axios.get<ProjectTypesListResponse>(
      PROJECT_TYPE_ENDPOINTS.list,
      {
        params,
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get all project type categories
 */
export async function getProjectTypeCategories(token: string): Promise<string[]> {
  try {
    const response = await axios.get<CategoriesResponse>(
      PROJECT_TYPE_ENDPOINTS.categories,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get project types by category
 */
export async function getProjectTypesByCategory(
  category: string,
  token: string
): Promise<ProjectType[]> {
  try {
    const response = await axios.get<ProjectTypesListResponse>(
      PROJECT_TYPE_ENDPOINTS.byCategory(category),
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get project type by ID
 */
export async function getProjectTypeById(
  id: string,
  token: string
): Promise<ProjectType> {
  try {
    const response = await axios.get<ProjectTypeResponse>(
      PROJECT_TYPE_ENDPOINTS.byId(id),
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Create a new project type (Platform Admin only)
 */
export async function createProjectType(
  data: CreateProjectTypeInput,
  token: string
): Promise<ProjectType> {
  try {
    const response = await axios.post<ProjectTypeResponse>(
      PROJECT_TYPE_ENDPOINTS.create,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Update project type (Platform Admin only)
 */
export async function updateProjectType(
  id: string,
  data: UpdateProjectTypeInput,
  token: string
): Promise<ProjectType> {
  try {
    const response = await axios.put<ProjectTypeResponse>(
      PROJECT_TYPE_ENDPOINTS.update(id),
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Delete project type (Platform Admin only)
 */
export async function deleteProjectType(id: string, token: string): Promise<void> {
  try {
    await axios.delete<MessageResponse>(PROJECT_TYPE_ENDPOINTS.delete(id), {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  } catch (error) {
    handleAPIError(error);
  }
}

export default {
  listProjectTypes,
  getProjectTypeCategories,
  getProjectTypesByCategory,
  getProjectTypeById,
  createProjectType,
  updateProjectType,
  deleteProjectType
};
